/** @jsxImportSource @emotion/react */
import React, { useState, useCallback, useMemo } from 'react'
import { css } from '@emotion/react'
import { Spin, App } from 'antd'
import { EditOutlined, PlusOutlined, LoadingOutlined } from '@ant-design/icons'
import colors from '@sportninja/common/constants/appColors'
import { ChevronDown, ChevronRight } from '../icons'
import type { VenueCardProps, TimeSlotEditState } from './types'
import { getVenueTimeSlotGroups } from './types'
import TimeSlotTableHeader from './TimeSlotTableHeader'
import TimeSlotRow from './TimeSlotRow'

/**
 * Venue Card Component
 * Shows venue-level time slots (grouped across all surfaces)
 */
const VenueCard: React.FC<VenueCardProps> = ({
  venue,
  timeSlots: allTimeSlots,
  timeSlotCounts: _timeSlotCounts,
  timeSlotsLoading,
  divisions,
  onEditVenue,
  onDeleteVenue: _onDeleteVenue,
  onDeleteFacility: _onDeleteFacility,
  onAddTimeSlotForVenue,
  onUpdateTimeSlotBatch,
  onDeleteTimeSlotBatch,
}) => {
  const { message } = App.useApp()

  // Venue expanded state
  const [isVenueExpanded, setIsVenueExpanded] = useState(true)

  // Time slot editing state (lifted from SurfaceSection)
  const [editingSlotId, setEditingSlotId] = useState<string | null>(null)
  const [editState, setEditState] = useState<TimeSlotEditState | null>(null)
  const [saveLoading, setSaveLoading] = useState(false)

  // Group identical time slots across facilities
  const venueTimeSlotGroups = useMemo(
    () => getVenueTimeSlotGroups(allTimeSlots, venue),
    [allTimeSlots, venue]
  )

  // Calculate totals
  const totalTimeSlots = venueTimeSlotGroups.length
  const totalSurfaces = venue.facilities?.length || 0

  // Toggle venue expanded state
  const toggleVenue = useCallback(() => {
    setIsVenueExpanded((prev) => !prev)
  }, [])

  // Facility IDs for this venue
  const facilityIds = useMemo(
    () => venue.facilities?.map((f) => f.id) || [],
    [venue.facilities]
  )

  // Start adding a new slot
  const handleAddNewSlot = useCallback(() => {
    setEditingSlotId('new')
    setEditState({
      slotId: null,
      frequency: 'weekly',
      days: [1, 3, 5],
      startTime: '09:00',
      endTime: '17:00',
      divisions: [],
    })
  }, [])

  // Start editing existing slot
  const handleEditSlot = useCallback((slot: (typeof allTimeSlots)[0]) => {
    const timeRange = slot.time_ranges?.[0]
    setEditingSlotId(slot.id)
    setEditState({
      slotId: slot.id,
      frequency: slot.recurrence_type,
      days: slot.days || [],
      startTime: timeRange?.start_time?.slice(0, 5) || '09:00',
      endTime: timeRange?.end_time?.slice(0, 5) || '17:00',
      divisions: slot.divisions || [],
    })
  }, [])

  // Save slot (create or update across all facilities)
  const handleSave = useCallback(async () => {
    if (!editState) {
      return
    }

    setSaveLoading(true)
    try {
      const data = {
        label: `Time Slot ${venueTimeSlotGroups.length + 1}`,
        recurrence_type: editState.frequency,
        days: editState.days,
        time_ranges: [
          {
            start_time: editState.startTime,
            end_time: editState.endTime,
          },
        ],
        divisions: editState.divisions,
      }

      if (editState.slotId) {
        // Find the group containing this slot and update all slots in it
        const group = venueTimeSlotGroups.find((g) =>
          g.slotIds.includes(editState.slotId!)
        )
        if (group) {
          await onUpdateTimeSlotBatch(group.slotIds, data)
        }
        message.success('Time slot updated across all surfaces')
      } else {
        // Create for all facilities in this venue
        await onAddTimeSlotForVenue(facilityIds, data)
        message.success('Time slot created for all surfaces')
      }

      setEditingSlotId(null)
      setEditState(null)
    } catch (err) {
      console.error('Failed to save time slot:', err)
      message.error('Failed to save time slot')
    } finally {
      setSaveLoading(false)
    }
  }, [
    editState,
    facilityIds,
    onAddTimeSlotForVenue,
    onUpdateTimeSlotBatch,
    venueTimeSlotGroups,
    message,
  ])

  // Cancel editing
  const handleCancel = useCallback(() => {
    setEditingSlotId(null)
    setEditState(null)
  }, [])

  // Delete slot (all matching across facilities)
  const handleDelete = useCallback(
    async (slotId: string) => {
      if (!window.confirm('Are you sure you want to delete this time slot?')) {
        return
      }
      try {
        const group = venueTimeSlotGroups.find((g) =>
          g.slotIds.includes(slotId)
        )
        if (group) {
          await onDeleteTimeSlotBatch(group.slotIds)
        }
        message.success('Time slot deleted from all surfaces')
      } catch (err) {
        console.error('Failed to delete time slot:', err)
        message.error('Failed to delete time slot')
      }
    },
    [onDeleteTimeSlotBatch, venueTimeSlotGroups, message]
  )

  // Icon button styles
  const iconButtonStyles = css`
    background: transparent;
    border: none;
    color: ${colors.PRIMARY_300};
    cursor: pointer;
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;

    &:hover {
      color: ${colors.PRIMARY_200};
    }
  `

  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        gap: 2px;
      `}
    >
      {/* Venue Header */}
      <div
        css={css`
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 8px 16px;
          background-color: ${colors.SECONDARY_400};
          border-radius: 8px;
          cursor: pointer;
          user-select: none;

          &:hover {
            background-color: #1e2733;
          }
        `}
        onClick={toggleVenue}
      >
        <div
          css={css`
            display: flex;
            align-items: center;
            gap: 16px;
          `}
        >
          {/* Left section: expand/collapse + name */}
          <div
            css={css`
              display: flex;
              align-items: center;
              gap: 8px;
              width: 319px;
            `}
          >
            <div
              css={css`
                width: 24px;
                height: 24px;
                display: flex;
                align-items: center;
                justify-content: center;
                flex-shrink: 0;
              `}
            >
              {isVenueExpanded ? (
                <ChevronDown size={24} />
              ) : (
                <ChevronRight size={24} />
              )}
            </div>

            <span
              css={css`
                font-family: 'Inter', sans-serif;
                font-size: 16px;
                font-weight: 500;
                line-height: 23.2px;
                color: ${colors.NEUTRAL_30};
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
                width: 253px;
              `}
            >
              {venue.name || venue.name_full}
            </span>
          </div>

          {/* Stats */}
          <div
            css={css`
              display: flex;
              align-items: center;
              gap: 16px;
            `}
          >
            <span
              css={css`
                font-family: 'Inter', sans-serif;
                font-size: 14px;
                font-weight: 400;
                line-height: 20.3px;
                color: ${colors.NEUTRAL_50};
              `}
            >
              {totalTimeSlots} Time Slot{totalTimeSlots !== 1 ? 's' : ''}
            </span>
            <span
              css={css`
                font-family: 'Inter', sans-serif;
                font-size: 14px;
                font-weight: 400;
                line-height: 20.3px;
                color: ${colors.NEUTRAL_50};
                width: 88px;
              `}
            >
              {totalSurfaces} Surface{totalSurfaces !== 1 ? 's' : ''}
            </span>
          </div>
        </div>

        {/* Action buttons */}
        <div
          css={css`
            display: flex;
            align-items: center;
            gap: 8px;
          `}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            type='button'
            onClick={handleAddNewSlot}
            css={iconButtonStyles}
            aria-label='Add time slot'
          >
            <PlusOutlined style={{ fontSize: 18 }} />
          </button>
          <button
            type='button'
            onClick={() => onEditVenue(venue)}
            css={iconButtonStyles}
            aria-label={`Edit ${venue.name}`}
          >
            <EditOutlined style={{ fontSize: 18 }} />
          </button>
        </div>
      </div>

      {/* Venue Content - Time Slots at venue level */}
      {isVenueExpanded && (
        <div
          css={css`
            display: flex;
            flex-direction: column;
          `}
        >
          {venue.facilities && venue.facilities.length > 0 ? (
            <div
              css={css`
                background-color: ${colors.SECONDARY_500};
              `}
            >
              {/* Table Header */}
              <TimeSlotTableHeader />

              {/* Loading state */}
              {timeSlotsLoading && (
                <div
                  css={css`
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    padding: 24px;
                  `}
                >
                  <Spin
                    indicator={
                      <LoadingOutlined style={{ fontSize: 16 }} spin />
                    }
                  />
                  <span
                    css={css`
                      margin-left: 8px;
                      color: ${colors.NEUTRAL_100};
                      font-size: 13px;
                    `}
                  >
                    Loading time slots...
                  </span>
                </div>
              )}

              {/* New slot row (when adding) */}
              {editingSlotId === 'new' && editState && (
                <TimeSlotRow
                  timeSlot={{
                    id: 'new',
                    label: 'New Time Slot',
                    recurrence_type: editState.frequency,
                    is_active: true,
                    days: editState.days,
                    time_ranges: [
                      {
                        id: 'new',
                        start_time: editState.startTime,
                        end_time: editState.endTime,
                        sort_order: 0,
                      },
                    ],
                    facility_id: facilityIds[0] || '',
                    schedule_id: '',
                  }}
                  index={0}
                  isEditing={true}
                  editState={editState}
                  divisions={divisions}
                  onEdit={() => {}}
                  onSave={handleSave}
                  onCancel={handleCancel}
                  onDelete={() => handleCancel()}
                  onEditStateChange={setEditState}
                  loading={saveLoading}
                />
              )}

              {/* Grouped time slots */}
              {!timeSlotsLoading &&
                venueTimeSlotGroups.map((group, index) => (
                  <TimeSlotRow
                    key={group.fingerprint}
                    timeSlot={group.representative}
                    index={editingSlotId === 'new' ? index + 1 : index}
                    isEditing={editingSlotId === group.representative.id}
                    editState={
                      editingSlotId === group.representative.id
                        ? editState
                        : null
                    }
                    divisions={divisions}
                    onEdit={() => handleEditSlot(group.representative)}
                    onSave={handleSave}
                    onCancel={handleCancel}
                    onDelete={() => handleDelete(group.representative.id)}
                    onEditStateChange={setEditState}
                    loading={saveLoading}
                  />
                ))}

              {/* Empty state */}
              {!timeSlotsLoading &&
                venueTimeSlotGroups.length === 0 &&
                editingSlotId !== 'new' && (
                  <div
                    css={css`
                      padding: 24px 48px;
                      text-align: center;
                    `}
                  >
                    <span
                      css={css`
                        color: ${colors.NEUTRAL_100};
                        font-family: 'Inter', sans-serif;
                        font-size: 13px;
                      `}
                    >
                      No time slots configured.{' '}
                      <button
                        type='button'
                        onClick={handleAddNewSlot}
                        css={css`
                          background: none;
                          border: none;
                          color: ${colors.PRIMARY_300};
                          cursor: pointer;
                          text-decoration: underline;
                          font-size: 13px;
                          padding: 0;

                          &:hover {
                            color: ${colors.PRIMARY_200};
                          }
                        `}
                      >
                        Add your first time slot.
                      </button>
                    </span>
                  </div>
                )}
            </div>
          ) : (
            <div
              css={css`
                padding: 24px 40px;
                background-color: ${colors.SECONDARY_500};
                text-align: center;
              `}
            >
              <span
                css={css`
                  color: ${colors.NEUTRAL_100};
                  font-family: 'Inter', sans-serif;
                  font-size: 13px;
                `}
              >
                No surfaces configured.{' '}
                <button
                  type='button'
                  onClick={() => onEditVenue(venue)}
                  css={css`
                    background: none;
                    border: none;
                    color: ${colors.PRIMARY_300};
                    cursor: pointer;
                    text-decoration: underline;
                    font-size: 13px;
                    padding: 0;

                    &:hover {
                      color: ${colors.PRIMARY_200};
                    }
                  `}
                >
                  Edit venue to add surfaces.
                </button>
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default VenueCard
